import { useState } from 'react';
import { PiInfoBold, PiTimerBold, PiWarningBold } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import { cn } from '@v2/utils/cn';
import V2Modal from '@v2/components/ui/V2Modal';
import { useI18n } from '@v2/providers/I18nProvider';

export type EntryPlusOneCarencia = {
    applicable: boolean;
    legacy_frozen?: boolean;
    grace_days: number;
    gap_days: number;
    within_window: boolean;
    days_left_in_window: number;
    days_over_window: number;
    days_until_remaining_due?: number | null;
    carencia_waived: boolean;
    carencia_months_detected?: number;
    effective_carencia_months?: number;
    late_trigger?: boolean;
    late_trigger_reason?: 'defaulted' | 'paid_after_grace' | string | null;
    enforced?: boolean;
    enforcement?: {
        reason?: string;
        original_final_commission?: number;
        adjusted_final_commission?: number;
        entry_commission?: number;
        remaining_commission?: number;
        seller_total?: number;
    } | null;
    entry_date?: string | null;
    remaining_due_date?: string | null;
};

type Props = {
    data: EntryPlusOneCarencia;
};

function formatBrl(value: number | undefined | null): string {
    if (value == null || Number.isNaN(value)) return '—';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function EntryPlusOneCarenciaBanner({ data }: Props) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const grace = data.grace_days;
    const late = Boolean(data.late_trigger);
    const frozen = Boolean(data.legacy_frozen);
    const enforced = Boolean(data.enforced) && !frozen;
    const pct = Math.min(100, Math.round((data.gap_days / Math.max(1, grace)) * 100));

    const dueLabel = (() => {
        const d = data.days_until_remaining_due;
        if (d == null) return null;
        if (d === 0) return t('v2.ploomes_deals.entry_plus_one.due_today');
        if (d > 0) return t('v2.ploomes_deals.entry_plus_one.due_in', { days: d });
        return t('v2.ploomes_deals.entry_plus_one.due_past', { days: Math.abs(d) });
    })();

    const lateReason = data.late_trigger_reason === 'defaulted'
        ? t('v2.ploomes_deals.entry_plus_one.late_defaulted')
        : data.late_trigger_reason === 'paid_after_grace'
          ? t('v2.ploomes_deals.entry_plus_one.late_paid', { grace })
          : null;

    const enforcementSummary = enforced && data.enforcement
        ? t('v2.ploomes_deals.entry_plus_one.enforced_summary', {
            entry: formatBrl(data.enforcement.entry_commission),
            remaining: formatBrl(data.enforcement.remaining_commission),
            original: formatBrl(data.enforcement.original_final_commission),
            total: formatBrl(data.enforcement.seller_total ?? data.enforcement.adjusted_final_commission),
        })
        : null;

    return (
        <>
            <div
                className={cn(
                    'rounded-xl border px-3 py-3',
                    (late || enforced) && !frozen
                        ? 'border-rose-300 bg-rose-50/90 dark:border-rose-700/50 dark:bg-rose-950/30'
                        : data.within_window
                          ? 'border-emerald-300 bg-emerald-50/80 dark:border-emerald-700/50 dark:bg-emerald-950/25'
                          : 'border-amber-300 bg-amber-50/80 dark:border-amber-700/50 dark:bg-amber-950/25',
                )}
            >
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 space-y-1">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-zinc-100">
                            <PiTimerBold className="h-4 w-4 shrink-0" />
                            {t('v2.ploomes_deals.entry_plus_one.title')}
                        </p>
                        <p className="text-xs text-gray-700 dark:text-zinc-300">
                            {t('v2.ploomes_deals.entry_plus_one.counter_within', {
                                gap: data.gap_days,
                                grace,
                            })}
                            {' · '}
                            {data.within_window
                                ? t('v2.ploomes_deals.entry_plus_one.counter_left', {
                                    left: data.days_left_in_window,
                                })
                                : t('v2.ploomes_deals.entry_plus_one.counter_over', {
                                    over: data.days_over_window,
                                    grace,
                                })}
                        </p>
                        {dueLabel && (
                            <p className="text-[11px] text-gray-600 dark:text-zinc-400">{dueLabel}</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {frozen ? (
                                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-800 dark:bg-sky-900/40 dark:text-sky-300">
                                    {t('v2.ploomes_deals.entry_plus_one.frozen_badge')}
                                </span>
                            ) : data.carencia_waived ? (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    {t('v2.ploomes_deals.entry_plus_one.waived_badge')}
                                </span>
                            ) : (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900 dark:bg-amber-900/40 dark:text-amber-300">
                                    {t('v2.ploomes_deals.entry_plus_one.applies_badge')}
                                </span>
                            )}
                            {late && !frozen && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                    <PiWarningBold className="h-3 w-3" />
                                    {t('v2.ploomes_deals.entry_plus_one.late_badge')}
                                </span>
                            )}
                            {enforced && (
                                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-900 dark:bg-rose-900/40 dark:text-rose-200">
                                    {t('v2.ploomes_deals.entry_plus_one.enforced_badge')}
                                </span>
                            )}
                        </div>
                        {lateReason && (
                            <p className="text-[11px] font-medium text-rose-700 dark:text-rose-300">{lateReason}</p>
                        )}
                        {enforcementSummary && (
                            <p className="text-[11px] font-medium text-rose-800 dark:text-rose-200">
                                {enforcementSummary}
                            </p>
                        )}
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => setOpen(true)}
                    >
                        <PiInfoBold className="me-1 h-3.5 w-3.5" />
                        {t('v2.ploomes_deals.entry_plus_one.explain_btn')}
                    </Button>
                </div>

                <div className="mt-2.5">
                    <div className="h-2 overflow-hidden rounded-full bg-white/80 dark:bg-zinc-900/60">
                        <div
                            className={cn(
                                'h-full rounded-full transition-all',
                                (late || enforced) && !frozen
                                    ? 'bg-rose-500'
                                    : data.within_window
                                      ? 'bg-emerald-500'
                                      : 'bg-amber-500',
                            )}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <p className="mt-1 text-[10px] tabular-nums text-gray-500 dark:text-zinc-400">
                        {data.gap_days}/{grace} dias
                    </p>
                </div>
            </div>

            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title={t('v2.ploomes_deals.entry_plus_one.modal_title')}
                description={t('v2.ploomes_deals.entry_plus_one.modal_subtitle', { grace })}
                size="lg"
                footer={(
                    <Button size="sm" onClick={() => setOpen(false)}>
                        {t('v2.ploomes_deals.entry_plus_one.modal_close')}
                    </Button>
                )}
            >
                <div className="space-y-3 text-sm leading-relaxed">
                    <p>{t('v2.ploomes_deals.entry_plus_one.modal_p1', { grace })}</p>
                    <p>{t('v2.ploomes_deals.entry_plus_one.modal_p2', { grace })}</p>
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100">
                        {t('v2.ploomes_deals.entry_plus_one.modal_p3')}
                    </p>
                    <p className="font-mono text-xs text-gray-600 dark:text-zinc-400">
                        {t('v2.ploomes_deals.entry_plus_one.modal_formula')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                        {t('v2.ploomes_deals.entry_plus_one.modal_p4')}
                    </p>
                </div>
            </V2Modal>
        </>
    );
}
